import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookOpenText, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PolicyFilters } from "./policies/PolicyFilters";
import { PolicyTable } from "./policies/PolicyTable";
import { PolicyUploadModal } from "./policies/PolicyUploadModal";

export default function PolicyLibraryPage() {
  const { user } = useAuth();
  const [options, setOptions] = useState(null);
  const [categories, setCategories] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({ q: "", category: "", home: "", status: "" });
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    Promise.all([api.get("/policies/options"), api.get("/policies/categories")])
      .then(([o, c]) => { setOptions(o.data); setCategories(c.data.categories); })
      .catch(() => toast.error("Could not load Policy Library settings"));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: debouncedQ || undefined, category: filters.category || undefined,
        home: filters.home || undefined, status: filters.status || undefined };
      const { data } = await api.get("/policies", { params });
      setPolicies(data.policies);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load policies");
    } finally { setLoading(false); }
  }, [debouncedQ, filters.category, filters.home, filters.status]);

  useEffect(() => { load(); }, [load]);

  const canUpload = options?.capabilities?.Create;
  const stats = useMemo(() => ({
    active: policies.filter((p) => p.status === "Active").length,
    pending: policies.filter((p) => p.status === "Pending Review").length,
    draft: policies.filter((p) => p.status === "Draft").length,
  }), [policies]);

  if (!options) return <div className="p-8 text-sm text-slate-500 animate-pulse">Loading Policy Library…</div>;

  return (
    <div className="space-y-5" data-testid="policy-library-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-h2 flex items-center gap-2"><BookOpenText size={22} className="text-sky-700" /> Policy Library</h1>
          <p className="text-sm text-slate-500 mt-1">Internal policies and procedures. Files are private and only available to authorized roles.</p>
        </div>
        <div className="flex items-center gap-3">
          {options.privileged && (
            <div className="hidden md:flex items-center gap-2 text-xs" data-testid="policy-stats">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">{stats.active} active</span>
              <span className="px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200">{stats.pending} pending</span>
              <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200">{stats.draft} draft</span>
            </div>
          )}
          {canUpload && (
            <button onClick={() => setUploading(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-700 text-white text-sm hover:bg-sky-800" data-testid="policy-upload-button">
              <Plus size={16} /> Upload policy
            </button>
          )}
        </div>
      </div>

      <PolicyFilters filters={filters} onChange={setFilters} categories={categories} homes={options.homes}
        statuses={options.statuses} privileged={options.privileged} count={policies.length} />
      <PolicyTable policies={policies} loading={loading} />

      {uploading && (
        <PolicyUploadModal onClose={() => setUploading(false)} categories={categories} options={options} user={user}
          onCreated={() => { setUploading(false); load(); }} />
      )}
    </div>
  );
}
